import { Installments } from '@/components';
import {
  ProductDefaultTabEnum,
  ProductQuery,
  ProductVariantQuery,
} from '@/graphql';
import { AttributeCategory } from '@/graphql/graphql';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Button } from 'rsuite';
import Rating from '../comments';
import DescriptionContent from '../description';
import styles from './secondary.module.scss';
type Props = {
  data: ProductQuery | undefined;
  variantData: ProductVariantQuery | undefined;
  id?: string;
  loading: boolean;
  onTabChange: (value: ProductDefaultTabEnum) => void;
  activeTabFromButton: ProductDefaultTabEnum;
};

interface Tab {
  label: string;
  isActive: boolean;
  count?: number | null;
  enum: ProductDefaultTabEnum;
}
type DisableTabState = {
  [key in ProductDefaultTabEnum]?: boolean;
};
type ExtendedAttributeCategory = AttributeCategory & {
  translation?: {
    name: string;
  };
};

const SecondaryInfo = (props: Props) => {
  const { data, id, activeTabFromButton, onTabChange, loading, variantData } =
    props;
  const [showAll, setShowAll] = useState(false);
  const [disableTab, setDisabledTab] = useState<DisableTabState>({
    [ProductDefaultTabEnum.SPECIFICATIONS]: false,
    [ProductDefaultTabEnum.DESCRIPTION]: false,
    [ProductDefaultTabEnum.INSTALLMENTS_AND_CREDIT]: false,
  });
  const t = useTranslations('Components.product.secondary-info');
  const locale = useLocale();
  const parsedDescription = JSON.parse(data?.product?.description || '[]') || [
    { blocks: [] },
  ];
  const [tabs, setTabs] = useState<Tab[]>([
    {
      label: t('specifications'),
      isActive: true,
      enum: ProductDefaultTabEnum.SPECIFICATIONS,
    },
    {
      label: t('description'),
      isActive: false,
      enum: ProductDefaultTabEnum.DESCRIPTION,
    },
    {
      label: t('installment'),
      isActive: false,
      enum: ProductDefaultTabEnum.INSTALLMENTS_AND_CREDIT,
    },
    {
      label: t('reviews'),
      isActive: false,
      count: null,
      enum: ProductDefaultTabEnum.REVIEWS,
    },
  ]);
  const [activeTab, setActiveTab] = useState(activeTabFromButton);

  useEffect(() => {
    setDisabledTab(getDisabledTabs() as DisableTabState);
    if (data?.product?.activeReviewsCount !== undefined) {
      setTabs(prevTabs => {
        const newTabs = [...prevTabs];
        newTabs[3] = {
          ...newTabs[3],
          count: data?.product?.activeReviewsCount,
        };
        return newTabs;
      });
    }
  }, [data, variantData]);

  const handleTabClick = useCallback(
    (index: ProductDefaultTabEnum) => {
      if (disableTab[index]) return;
      const newTabs = tabs.map(tab => ({
        ...tab,
        isActive: tab.enum === index,
      }));
      setTabs(newTabs);
      setActiveTab(index);
      onTabChange(index);
    },
    [tabs, setTabs, setActiveTab, onTabChange, disableTab]
  );

  useEffect(() => {
    handleTabClick(activeTabFromButton);
  }, [activeTabFromButton]);

  const allAttributeCategories = data?.product?.attributesCategories?.filter(
    category => category !== null
  ) as ExtendedAttributeCategory[];

  const filteredCategoriesWithSpecs =
    allAttributeCategories?.filter(category => {
      const categoryAttributes = data?.product?.attributes.filter(
        attr => attr?.attribute?.category?.id === category.id
      );

      const hasSpecs = categoryAttributes?.some(
        attr => attr?.values && attr.values.length > 0
      );
      return hasSpecs;
    }) || [];

  const groupedCategories = filteredCategoriesWithSpecs?.reduce(
    (acc: ExtendedAttributeCategory[][], category, index) => {
      if (index % 2 === 0) {
        acc.push(
          filteredCategoriesWithSpecs?.slice(
            index,
            index + 2
          ) as AttributeCategory[]
        );
      }
      return acc;
    },
    []
  );
  const categoriesToShow = showAll
    ? groupedCategories
    : groupedCategories?.slice(0, 2);

  const nullCategoryAttributes =
    data?.product?.attributes?.filter(
      attr => attr?.attribute?.category === null
    ) || [];

  const renderNullCategory = () => {
    if (nullCategoryAttributes.length === 0) {
      return null;
    }
    const dataCharacteristics = [
      {
        category: t('others'),
        specs: nullCategoryAttributes
          ?.filter(attr => attr?.values && attr.values.length > 0)
          ?.map(attr => {
            return {
              title: attr?.attribute?.name,
              value: attr?.values?.map(value => value.name).join(', '),
            };
          }),
      },
    ];

    return dataCharacteristics.map((section, sectionIndex) => (
      <div key={`null-category-${sectionIndex}`} className={styles.div}>
        <b className={styles.title}>{t('others')}</b>
        {section?.specs?.map((spec, idx) => (
          <div key={idx} className={styles.row}>
            <div className={styles.title1}>{spec.title}</div>
            <div className={styles.rowChild} />
            <div className={styles.value}>{spec.value}</div>
          </div>
        ))}
      </div>
    ));
  };
  const shouldRenderNullCategory = showAll;

  function getDisabledTabs() {
    let newDisabledTab = {};
    if (!loading && data) {
      newDisabledTab = {
        [ProductDefaultTabEnum.SPECIFICATIONS]: !categoriesToShow?.length,
        [ProductDefaultTabEnum.DESCRIPTION]:
          parsedDescription?.blocks?.every(
            (block: any) => block?.data?.text === ''
          ) || parsedDescription?.length === 0,
        [ProductDefaultTabEnum.INSTALLMENTS_AND_CREDIT]:
          !variantData?.productVariant?.channelListings?.[0]?.installment
            ?.length,
      };
    }

    return newDisabledTab;
  }

  useEffect(() => {
    if (!loading && disableTab[activeTab]) {
      for (const tab of tabs) {
        if (!disableTab[tab.enum]) {
          setActiveTab(tab.enum);
          onTabChange(tab.enum);
          break;
        }
      }
    }
  }, [activeTab, disableTab, tabs, onTabChange, loading]);

  return (
    <div className={styles.secondaryWrapper} id={id}>
      <div className={styles.tabs}>
        {tabs.map((item, index) => (
          <Button
            key={index}
            type="button"
            disabled={disableTab[item.enum]}
            onClick={() => handleTabClick(item.enum)}
            className={`${styles.tab} ${
              item.isActive ? styles.tabActive : ''
            } ${disableTab[item.enum] ? styles.disabledTab : ''}`}
          >
            {item.label}
            {(item.count ?? 0) > 0 && (
              <div className={styles.label}>
                ({data?.product?.activeReviewsCount})
              </div>
            )}
          </Button>
        ))}
      </div>
      {activeTab === ProductDefaultTabEnum.SPECIFICATIONS && (
        <div className={styles.body}>
          <div className={styles.frameParent}>
            {loading ? (
              Array(2)
                .fill(null)
                .map((_, index) => (
                  <div key={`skeleton-${index}`} className={styles.row}>
                    {Array(2)
                      .fill(null)
                      .map((_, index) => (
                        <div className={styles.div} key={index}>
                          <Skeleton
                            width={150}
                            height={24}
                            className={styles.skeleton}
                          />
                          {Array(3)
                            .fill(null)
                            .map((_, specIndex) => (
                              <div
                                key={`spec-skeleton-${specIndex}`}
                                className={styles.row}
                              >
                                <Skeleton
                                  width={200}
                                  height={16}
                                  className={styles.skeleton}
                                />
                                <div className={styles.rowChild} />
                                <Skeleton
                                  width={100}
                                  height={16}
                                  className={styles.skeleton}
                                />
                              </div>
                            ))}
                        </div>
                      ))}
                  </div>
                ))
            ) : (
              <>
                {categoriesToShow?.map((categoryGroup, groupIndex) => (
                  <div key={groupIndex} className={styles.row}>
                    {categoryGroup?.map(category => {
                      if (!category) {
                        return null;
                      }
                      const categoryAttributes =
                        data?.product?.attributes.filter(
                          attr => attr?.attribute?.category?.id === category.id
                        );

                      const dataCharacteristics = [
                        {
                          category: category.name,
                          specs: categoryAttributes
                            ?.filter(
                              attr => attr?.values && attr.values.length > 0
                            )
                            ?.map(attr => {
                              return {
                                title:
                                  locale === 'ru'
                                    ? attr?.attribute?.name
                                    : attr?.attribute?.translation?.name,
                                value: attr?.values
                                  ?.map(value =>
                                    locale === 'ru'
                                      ? value.name
                                      : value?.translation?.name
                                  )
                                  .join(', '),
                              };
                            }),
                        },
                      ].filter(
                        section => section.specs && section.specs.length > 0
                      );

                      if (dataCharacteristics.length === 0) {
                        return null; // Не рендерим категорию, если нет характеристик
                      }

                      return dataCharacteristics.map(
                        (section, sectionIndex) => (
                          <div
                            key={`${category.id}-${sectionIndex}`}
                            className={styles.div}
                          >
                            <b className={styles.title}>
                              {locale === 'ru'
                                ? category?.name
                                : category?.translation?.name}
                            </b>
                            {section?.specs?.map((spec, idx) => (
                              <div key={idx} className={styles.row}>
                                <div className={styles.title1}>
                                  {spec.title}
                                </div>
                                <div className={styles.rowChild} />
                                <div className={styles.value}>{spec.value}</div>
                              </div>
                            ))}
                          </div>
                        )
                      );
                    })}
                  </div>
                ))}
                {shouldRenderNullCategory && (
                  <div className={styles.row}>{renderNullCategory()}</div>
                )}
              </>
            )}
          </div>
          {!loading && groupedCategories?.length > 0 && (
            <Button
              className={styles.link}
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? t('hide') : t('show-all')}
            </Button>
          )}
        </div>
      )}
      {activeTab === ProductDefaultTabEnum.REVIEWS && <Rating data={data} />}
      {activeTab === ProductDefaultTabEnum.DESCRIPTION && (
        <DescriptionContent data={data} />
      )}
      {activeTab === ProductDefaultTabEnum.INSTALLMENTS_AND_CREDIT && (
        <Installments variantData={variantData} productData={data} />
      )}
    </div>
  );
};

export default SecondaryInfo;
